
import StyleProvider from '@/StyleProvider.vue';
import TheApp from './TheApp.vue';
import Vue, { PropType } from 'vue';
import i18n from '@/i18n';
import isValidLanguageValidator from '@/validators/langLocaleValidator';
import { mapActions, mapState, PiniaVuePlugin } from 'pinia';
import { useStore as useLangStore } from '@/stores/lang';
import { FlowStep } from './typings/index';
import { FlowStarterHeader } from './components/Header';
import { FlowStarterProgressIndicator } from './components/ProgressIndicator';
import { FlowStarterFooter } from './components/Footer';
import { OrderOverview } from '../order-overview';
import { UploadLogo } from '../upload-logo';
import { MethodResult } from './typings/index';
import { priceDetails, pdpChoices } from '@/setters';
import { mapStores } from 'pinia';
import { pinia } from '@/pinia';
import { base64StringToBlob } from 'blob-util';
import customersAPI from '@/services/CustomersService';
import smallDetailsService from '@/services/SmallDetailsService';
import { Personalization } from '@midocean/webapps-shared-domain';
import { VFileUploaderFile } from '@/components/w2pFileUploader';
import {
  FileUploadedPayload,
  SelectedPrintPositionData,
} from '../upload-logo/typings/index';
import { CustomersServiceError } from '../../services/interceptor-handlers/CustomersServiceErrorHandler';
import { ProductPrintPositionSetterType } from '@/setters/typings/common/ProductPrintPositionSetterType';
import {
  useStore as useProductStore,
  attachActionSubscriber as attachProductActionSubscriber,
} from '@/stores/product';
import { useStore as useAuthStore } from '@/stores/auth';
import { ArtworkResult } from '@/components/w2pFileUploader';
import { ProductColorSetterType } from '@/setters/typings/common/ProductColorsSetterType';
import {
  constructLogoOnlyNewPersonalizationForm,
  constructFullDesignNewPersonalizationForm,
} from '@/construct-methods';
import { SmallDetailsLogoOnly } from '@/components/SmallDetails';

import { SupportedLangCodes } from '../../constants/languageCodes';
import ToolsAlt20 from '@carbon/icons-vue/es/tools--alt/20';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import { WizardPayload } from '../logo-library/typings';
import { SmallDetailsPayload } from '@/typings/SmallDetails';
import { getUtcDateTime } from '@/utils/personalizationHelper';
import FullDesign from './components/FullDesignUploader/FullDesign.vue';
import { ArtworkPosition } from '@/components/w2pFileUploader/typings/Artwork';
import {
  PrintTechnique,
  SmallDetailsPrintPosition,
  ProductPrintVariant,
  ProductPrintData,
  CreatePersonalizationForm,
  SmallDetailsRepresentation,
} from '@midocean/webapps-shared-domain';
import { LOGO_ONLY, FULL_DESIGN } from '@/constants/flow-starter';

Vue.use(PiniaVuePlugin);
// Vue.use(VModal, {
//   dynamic: true,
//   injectModalsContainer: true,
// });

export default Vue.extend({
  components: {
    StyleProvider,
    TheApp,
    FlowStarterHeader,
    FlowStarterProgressIndicator,
    FlowStarterFooter,
    OrderOverview,
    UploadLogo,
    ToolsAlt20,
    SmallDetailsLogoOnly,
    FullDesign,
  },

  props: {
    languageCode: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'none',
      validator: (value: string) => {
        if (!isValidLanguageValidator(value)) {
          console.error(
            'Wrong value for the `lang` prop. ' + `Given: "${value}".`
          );
        }
        return true;
      },
    },
    personalizationId: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    priceDetails,
    pdpChoices,
    wizardOptions: {
      type: Object,
      required: true,
      default() {
        return {
          showCurrentVersion: true,
          showNextVersion: false,
        };
      },
    },
    sapCustomerId: {
      type: String,
      required: true,
    },
    tok: {
      type: String,
      required: true,
    },
    isFullDesignPathEnabled: {
      type: Boolean,
      default: false,
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
    isFullDesignWithArtworksPathEnabled: {
      type: Boolean,
      default: false,
    },
    selectedFlow: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      headerTitle: '',
      resourcesObtained: false,
      flowSteps: [
        {
          name: 'select',
          isCompleted: false,
          isInvalid: false,
        },
        {
          name: 'upload-logo',
          isCompleted: false,
          isInvalid: false,
        },
        {
          name: 'order-overview',
          isCompleted: false,
          isInvalid: false,
        },
      ] as FlowStep[],
      isLogoOnlySelected: false,
      isFullDesignSelected: false,
      isSpinnerActive: false,
      methodResults: {
        logoOnly: {
          methodName: 'logoOnly',
          isSelected: false,
          isInvalid: false,
        },
        personalise: {
          methodName: 'personalise',
          isSelected: false,
          isInvalid: false,
        },
        personaliseNew: {
          methodName: 'personaliseNew',
          isSelected: false,
          isInvalid: false,
        },
        fullDesign: {
          methodName: 'fullDesign',
          isSelected: false,
          isInvalid: false,
        },
        fullDesignArtworks: {
          methodName: 'fullDesignArtworks',
          isSelected: false,
          isInvalid: false,
        },
      } as MethodResult,
      selectedMethod: this.wizardOptions.showCurrentVersion
        ? 'Personalise'
        : 'PersonaliseNew',
      isMethodSelected: true,
      currentStep: 'Select',
      previousStepExists: false,
      nextStepExists: true,
      isFileValid: false,
      isFileUploaded: false,
      selectedStepIndex: 0,
      selectedStepName: 'select',
      showProgressIndicator: false,
      validationPipelineResults: [] as any[],
      newPersonalization: {} as Personalization,
      isCreatePersonalizationSuccessful: false,
      productPrintData: {} as ProductPrintData,
      uploadedArtworks: [] as ArtworkResult[],
      uploadedFileColors: '',
      context: 'Customer',
      errors: [] as CustomersServiceError[],
      numberOfColorsPerPrintPosition: [] as {
        positionId: string;
        numberOfColors: number;
      }[],
      instructions: '',
      numbeOfProcessedFiles: 0,
      isAnalysisInProgress: false,
      isDetailLost: false,
      smallDetailsPrintPositions: [] as SmallDetailsPrintPosition[],
      smallDetailsResults: [] as SmallDetailsPayload[],
      smallDetailsResult: null as SmallDetailsPayload | null,
      isUploading: false,
      fullDesignMockup: {} as VFileUploaderFile,
      artworkPositionMap: [] as ArtworkPosition[],
      isSameLogoForAllPrintPositions: false,
    };
  },

  computed: {
    ...mapState(useLangStore, ['languageInUse']),
    ...mapStores(useProductStore),
    ...mapState(useAuthStore, ['isTokValid']),
    hasPdpChoicesRequiredData(): boolean {
      const pdp = this.pdpChoices;
      return (
        pdp != null &&
        typeof pdp === 'object' &&
        pdp.masterProductSKU !== '' &&
        Array.isArray(pdp.productPrintPositions) &&
        pdp.productPrintPositions.length > 0
      );
    },
    isPreSelectedFlow(): boolean {
      const flow = this.selectedFlow?.valueOf();
      return flow === LOGO_ONLY || flow === FULL_DESIGN;
    },
    isReadyToShow(): boolean {
      return (
        this.resourcesObtained &&
        (!this.isPreSelectedFlow || this.hasPdpChoicesRequiredData)
      );
    },
    isConfirmed(): boolean {
      return this.smallDetailsResult?.approvedAt != null;
    },
    uploadedFileData(): any {
      if (this.isSameLogoForAllPrintPositions) {
        return this.pdpChoices.productPrintPositions.map(
          (pos: ProductPrintPositionSetterType) => {
            return {
              ...this.uploadedArtworks[0],
              position: pos?.printPositionSAPCode,
            };
          }
        );
      } else {
        return this.uploadedArtworks.map((art) => {
          const artPos = this.artworkPositionMap.find(
            (pos) =>
              pos.fileName === art.fileName && pos.position === art.position
          );
          return { ...art, position: artPos?.position };
        });
      }
    },
  },
  watch: {
    selectedStepIndex() {
      this.stepManager();
    },

    smallDetailsResult() {
      this.isDetailLost =
        this.smallDetailsResult != null &&
        (this.smallDetailsResult.status == 'Bad' ||
          this.smallDetailsResult.status == 'Average');
      this.flowSteps[2].isInvalid = this.isDetailLost;
    },

    isTokValid(newValue) {
      if (!newValue) {
        this.$emit('token-invalid-or-expired');
      }
    },
    tok(newValue) {
      if (newValue !== '') {
        this.setTok(this.tok);
        this.getTranslations();
      }
    },

    pdpChoices: {
      async handler(newValue: unknown) {
        const pdp = newValue as
          | { masterProductSKU?: string; productPrintPositions?: unknown[] }
          | null
          | undefined;
        const hasRequiredData =
          pdp != null &&
          typeof pdp === 'object' &&
          pdp.masterProductSKU !== '' &&
          Array.isArray(pdp.productPrintPositions) &&
          pdp.productPrintPositions.length > 0;

        if (!hasRequiredData) {
          return;
        }
        try {
          this.numberOfColorsPerPrintPosition =
            this.pdpChoices.productPrintPositions.map((p: any) => ({
              positionId: p.printPositionSAPCode,
              numberOfColors: +p.numberOfColors,
            }));
          if (DoSmallDetailsPart) {
            await this.getLimitations();
            if (
              this.selectedMethod === 'Logo only' ||
              this.selectedMethod === 'Logo only - new flow'
            ) {
              this.updateSelectedMethod(this.selectedMethod);
            }
          }
        } catch (e: unknown) {
          console.error((e as Error).message);
        }
      },
      immediate: true,
      deep: true,
    },
  },

  async mounted() {
    attachProductActionSubscriber();

    this.setSapCustomerId(this.sapCustomerId);
    this.setTok(this.tok);

    await this.getTranslations();
    this.setLanguageLocale(this.languageCode);

    // this.$modal.show('flow-starter-modal');

    this.numberOfColorsPerPrintPosition =
      this.pdpChoices.productPrintPositions.map((p: any) => {
        return {
          positionId: p.printPositionSAPCode,
          numberOfColors: +p.numberOfColors,
        };
      });

    this.currentStep = 'select';
    this.selectedStepName = 'select';
    this.headerTitle = this.$t('flow-starter.header-title').toString();

    if (this.selectedFlow?.valueOf() === LOGO_ONLY) {
      this.updateSelectedMethod('Logo only - new flow');
      this.goToNextStep();
    }

    if (this.selectedFlow?.valueOf() === FULL_DESIGN) {
      this.updateSelectedMethod('Full design - new flow');
      this.goToNextStep();
    }

    console.log('Flow starter pdp choices mounted: ', this.pdpChoices);
  },
  methods: {
    ...mapActions(useLangStore, [
      'setLanguageLocale',
      'setDefaultTranslations',
    ]),
    ...mapActions(useAuthStore, ['setSapCustomerId', 'setTok']),
    ...mapActions(useProductStore, ['getProduct']),
    async getTranslations() {
      await this.setDefaultTranslations();

      if (
        process.env.VUE_APP_USE_LOCAL_TRANSLATIONS === 'false' &&
        this.languageCode !== 'en'
      ) {
        var translationsJson = await customersAPI.getTranslations(
          this.languageCode
        );
        this.$i18n.setLocaleMessage(this.languageCode, translationsJson.data);
      }
      this.resourcesObtained = true;
    },
    additionalFilesUploaded(file: VFileUploaderFile) {
      this.fullDesignMockup = file.file ? file : ({} as VFileUploaderFile);
      this.updateNextButton();
    },
    removeUploadedFile(position: string) {
      if (this.isSameLogoForAllPrintPositions)
        this.isSameLogoForAllPrintPositions = false;
      const artPos = this.artworkPositionMap.find(
        (art: ArtworkPosition) => art.position === position
      );
      this.artworkPositionMap = this.artworkPositionMap.filter(
        (art: ArtworkPosition) => artPos?.fileName !== art.fileName
      );
      this.uploadedArtworks = this.uploadedArtworks.filter(
        (art) => art.fileName !== artPos?.fileName
      );
      this.updateNextButton();
    },
    updateNextButton() {
      this.isFileUploaded =
        (this.fullDesignMockup.file &&
          this.uploadedArtworks.length ===
            this.generateSelectedPrintPositionData().length) ||
        (this.fullDesignMockup.file &&
          this.uploadedArtworks.length > 0 &&
          this.isSameLogoForAllPrintPositions);
    },
    closeModal() {
      this.$emit('flow-starter:close-modal');
    },
    async getLimitations() {
      if (
        this.pdpChoices === undefined ||
        this.pdpChoices.masterProductSKU === '' ||
        !this.pdpChoices.productPrintPositions?.length
      ) {
        return;
      }

      this.smallDetailsPrintPositions = [];

      const productPrintData = await this.getProduct(
        this.pdpChoices.masterProductSKU
      );

      const chosenPrintPositions =
        productPrintData.productPrintPositions.filter((p) => {
          const result = this.pdpChoices.productPrintPositions.find(
            (x: ProductPrintPositionSetterType) =>
              x.printPositionSAPCode === p.name
          );

          return result === undefined ? false : true;
        });

      const chosenPrintTechniques = chosenPrintPositions.map((p) => {
        const result = this.pdpChoices.productPrintPositions.find(
          (x: ProductPrintPositionSetterType) =>
            x.printPositionSAPCode === p.name
        );
        const technique = p.printTechniques.find(
          (t) =>
            t.printTechnique.code === result.printTechniqueSAPCode &&
            t.printTechnique.limitation
        );

        return technique === undefined
          ? undefined
          : ({
              sapCode: p.name,
              printArea: p.printArea,
              negativeLimitMM:
                technique.printTechnique.limitation.negativeLimitMM,
              positiveLimitMM:
                technique.printTechnique.limitation.positiveLimitMM,
              originalImageDpi: p.originalImageDpi,
              originalImageWebDpi: p.originalImageWebDpi,
              originalImageWidthPx: p.originalImageWidthPx,
              ignoreColors: technique.printTechnique.limitation.ignoreColors,
            } as SmallDetailsPrintPosition);
      });

      chosenPrintTechniques.forEach(
        (p: SmallDetailsPrintPosition | undefined) => {
          if (p !== undefined) {
            this.smallDetailsPrintPositions.push(p);
          }
        }
      );
    },
    redirectToWizard() {
      var wizardVersion = this.wizardOptions.showCurrentVersion ? 'V1' : 'V2';

      if (this.isUploading) return;
      if (this.uploadedArtworks.length > 0) {
        const payload = this.uploadedArtworks.map((artwork) => {
          return {
            ...artwork,
            id: artwork.personalizationArtworkId,
            title: artwork.fileName,
            fileName: artwork.fileName,
            pngFileUrl: artwork.originalFileUrl,
          };
        });
        this.$emit('redirect-to-wizard', {
          logoOnlyArtwork: payload,
          version: wizardVersion,
          fullScreen: wizardVersion === 'V2',
        });
      } else {
        this.$emit('redirect-to-wizard', {
          version: wizardVersion,
          fullScreen: wizardVersion === 'V2',
        });
      }
    },
    goToPreviousStep() {
      if (
        this.selectedMethod === 'Full design artworks' ||
        this.selectedMethod === 'Full design - new flow'
      ) {
        this.uploadedArtworks = [];
        this.isSameLogoForAllPrintPositions = false;
        this.artworkPositionMap = [];
      }
      this.selectedStepIndex = this.selectedStepIndex - 1;

      const previousStep = this.flowSteps[this.selectedStepIndex];

      previousStep.isCompleted = false;

      // Disabling Next button in case we go to the previous step, by setting that a file has not been chosen yet
      if (this.selectedStepIndex === 1) {
        this.isFileValid = false;
        this.isFileUploaded = false;
      }
    },
    goToNextStep() {
      if (this.selectedMethod === 'Personalise') {
        this.$emit('wizard:ordered');
        this.$emit('wizard:open', {
          version: 'V1',
          fullScreen: false,
        });
        return;
      }
      if (this.selectedMethod === 'PersonaliseNew') {
        this.$emit('wizard:open', {
          version: 'V2',
          fullScreen: true,
        });
        return;
      }

      if (
        (this.selectedMethod === 'Full design artworks' ||
          this.selectedMethod === 'Full design - new flow') &&
        this.isSameLogoForAllPrintPositions
      ) {
        this.uploadedArtworks = this.pdpChoices.productPrintPositions.map(
          (pos: ProductPrintPositionSetterType) => {
            return {
              ...this.uploadedArtworks[0],
              position: pos?.printPositionSAPCode,
            };
          }
        );
      }

      this.selectedStepIndex = this.selectedStepIndex + 1;

      const previousStep = this.flowSteps[this.selectedStepIndex - 1];

      previousStep.isCompleted = true;
      this.showProgressIndicator = true;

      this.headerTitle = this.getMethodTranslation(
        this.selectedMethod
      ).toString();

      // Disabling Next button for the Upload step, due to the if condition for disabling of the Next button
      this.isMethodSelected = false;
    },
    getMethodTranslation(selectedMethod: string) {
      if (selectedMethod === 'Logo only') {
        return this.$t('flow-starter.methods.we-create-your-proof');
      }
      if (selectedMethod === 'Logo only - new flow') {
        return this.$t(
          'flow-starter.methods.we-create-your-proof-logo-only-flow'
        );
      }
      if (
        selectedMethod === 'Full design' ||
        selectedMethod === 'Full design artworks' ||
        selectedMethod === 'Full design - new flow'
      ) {
        return this.$t('flow-starter.methods.full-design');
      }
      return selectedMethod;
    },
    stepManager() {
      const stepToMoveTo = this.flowSteps[this.selectedStepIndex];

      this.selectedStepName = stepToMoveTo.name;

      // remove previous small detail results
      if (this.selectedStepIndex === 1) {
        this.smallDetailsResults = [];
        this.smallDetailsResult = null;
        this.isAnalysisInProgress = false;
        this.flowSteps[2].isInvalid = false;
      }

      // check small details
      if (
        this.selectedStepIndex === 2 &&
        this.isLogoOnlySelected &&
        this.smallDetailsPrintPositions.length > 0 &&
        this.smallDetailsResults.length === 0
      ) {
        this.checkSmallDetails();
      }

      if (this.selectedStepIndex === 0) {
        this.previousStepExists = false;
        this.isMethodSelected = false;
        this.showProgressIndicator = false;

        this.headerTitle = this.$t('flow-starter.header-title').toString();
      } else if (
        this.selectedFlow?.valueOf() !== LOGO_ONLY &&
        this.selectedFlow?.valueOf() !== FULL_DESIGN
      ) {
        this.previousStepExists = true;
      }

      if (this.selectedStepIndex >= this.flowSteps.length - 1) {
        this.nextStepExists = false;
      } else {
        this.nextStepExists = true;
      }
    },

    async checkSmallDetails() {
      this.isAnalysisInProgress = true;

      // calculate width for every position
      this.smallDetailsPrintPositions.forEach(
        (position: SmallDetailsPrintPosition) => {
          position.calculatedWidth = smallDetailsService.calculateArtworkSize(
            this.uploadedArtworks[0] as unknown as WizardPayload,
            position
          );
        }
      );

      // map artworks to print positions
      this.pdpChoices.productPrintPositions.forEach(
        (item: ProductPrintPositionSetterType, index: number) => {
          var smallDetailsPrintPosition = this.smallDetailsPrintPositions.find(
            (x) => x.sapCode === item.printPositionSAPCode
          );
          if (smallDetailsPrintPosition) {
            smallDetailsPrintPosition.artworkId =
              this.uploadedArtworks[index].personalizationArtworkId;
            smallDetailsPrintPosition.file = base64StringToBlob(
              this.uploadedArtworks[index].pngFile,
              'image/png'
            );
            smallDetailsPrintPosition.ignoreColors =
              smallDetailsPrintPosition.ignoreColors ||
              Array.from(
                new Set(
                  this.uploadedArtworks[index].pmsColorsInPngFile.map(
                    (obj) => obj.name
                  )
                )
              ).length === 1;
          }
        }
      );

      this.smallDetailsResults = await smallDetailsService.analyzeSmallDetails(
        this.smallDetailsPrintPositions.filter((p) => p.artworkId !== undefined)
      );
      if (this.smallDetailsResults.length > 0) {
        this.smallDetailsResult = this.smallDetailsResults[0];
      }

      this.isAnalysisInProgress = false;
    },

    addToCart() {
      this.isSpinnerActive = true;

      // Check what personalization method was chosen and call the corresponding endpoint of the Customers API
      if (
        this.selectedMethod === 'Logo only' ||
        this.selectedMethod === 'Logo only - new flow'
      ) {
        // If the file is valid, then we will create a personalization with that file
        if (this.isFileValid) {
          this.createPersonalization(this.uploadedArtworks)
            .then((newPersonalization) => {
              // Mapped new personalizationId returned from the backend to the this.newPersonalization data attribute that will be emitted back to PDP
              if (newPersonalization.data.id !== '') {
                this.isCreatePersonalizationSuccessful = true;
                this.newPersonalization = newPersonalization.data;
              }

              // Emit success event back to Fenego
              this.emitPersonalizationId();
            })
            .catch((error: CustomersServiceError) => {
              console.log(error);
              // Assign the adapted error into the error array
              this.errors = [error];

              // Stop the spinner
              this.isSpinnerActive = false;

              // This will enable the Add to cart button immediately after the personalization creation process fails
              this.isCreatePersonalizationSuccessful = false;
            });
        }
      } else if (
        this.selectedMethod === 'Full design' ||
        this.selectedMethod === 'Full design artworks' ||
        this.selectedMethod === 'Full design - new flow'
      ) {
        this.createPersonalizationFullDesign(this.uploadedArtworks)
          .then((newFullDesignPersonalization) => {
            // Mapped new personalizationId returned from the backend to the this.newPersonalization data attribute that will be emitted back to PDP
            if (newFullDesignPersonalization.data.id !== '') {
              this.isCreatePersonalizationSuccessful = true;
              this.newPersonalization = newFullDesignPersonalization.data;
            }

            //Emit success event back to Fenego
            this.emitPersonalizationId();
          })
          .catch((error: CustomersServiceError) => {
            // Assign the adapted error into the error array
            this.errors = [error];

            // Stop the spinner
            this.isSpinnerActive = false;

            // This will enable the Add to cart button immediately after the personalization creation process fails
            this.isCreatePersonalizationSuccessful = false;
          });
      }
    },
    emitPersonalizationId() {
      if (this.isCreatePersonalizationSuccessful) {
        // Determine what event to send back to Fenego by comparing what path was selected
        const eventName: string =
          this.selectedMethod === 'Logo only' ||
          this.selectedMethod === 'Logo only - new flow'
            ? 'customer-with-logo:ordered'
            : 'customer-with-fulldesign:ordered';

        // Emits the event back to Fenego
        this.$emit(eventName, {
          personalizationId: this.newPersonalization.id,
        });
      }
    },
    updateSelectedMethod(selectedMethod: string) {
      this.selectedMethod = selectedMethod;

      // hide/show small details step by mutating in place to preserve isCompleted states
      const needsSmallDetails =
        (this.selectedMethod === 'Logo only' ||
          this.selectedMethod === 'Logo only - new flow') &&
        this.smallDetailsPrintPositions.length > 0;
      const smallDetailsIndex = this.flowSteps.findIndex(
        (s) => s.name === 'small-details'
      );
      if (needsSmallDetails && smallDetailsIndex === -1) {
        this.flowSteps.splice(2, 0, {
          name: 'small-details',
          isCompleted: false,
          isInvalid: false,
        });
      } else if (!needsSmallDetails && smallDetailsIndex !== -1) {
        this.flowSteps.splice(smallDetailsIndex, 1);
      }

      if (this.selectedMethod !== 'Select personalise method') {
        // Only update isMethodSelected when on the select step; when called
        // from the pdpChoices watcher after navigation, the upload step must
        // remain gated on isFileUploaded and not be unlocked by this flag.
        if (this.selectedStepIndex === 0) {
          this.isMethodSelected = true;
        }

        if (
          this.selectedMethod === 'Logo only' ||
          this.selectedMethod === 'Logo only - new flow'
        ) {
          this.isLogoOnlySelected = true;
          this.isFullDesignSelected = false;

          // This will change the progress indicator step to Upload file instead of Upload logo
          this.flowSteps[1].name = 'upload-logo';
        }

        if (
          this.selectedMethod === 'Full design' ||
          this.selectedMethod === 'Full design artworks' ||
          this.selectedMethod === 'Full design - new flow'
        ) {
          this.isFullDesignSelected = true;
          this.isLogoOnlySelected = false;

          // This will change the progress indicator step to Upload file instead of Upload logo
          this.flowSteps[1].name = 'upload-file';
        }

        this.nextStepExists = true;

        return;
      }

      this.isMethodSelected = false;
      this.nextStepExists = true;
      this.showProgressIndicator = false;
    },

    fileUploadedSuccessfully(fileUploadedPayload: FileUploadedPayload) {
      // if upload was cancelled, there won't be artworks
      if (fileUploadedPayload.uploadedArtworks.length === 0) {
        return;
      }
      this.numbeOfProcessedFiles = this.numbeOfProcessedFiles + 1;
      if (
        this.generateSelectedPrintPositionData().length ===
        this.numbeOfProcessedFiles
      ) {
        this.isFileUploaded = true;
        this.numbeOfProcessedFiles = 0;
      }
      this.artworkPositionMap.push({
        fileName: fileUploadedPayload.uploadedArtworks[0].fileName,
        position: fileUploadedPayload.position,
      } as ArtworkPosition);
      if (
        this.isFullDesignWithArtworksPathEnabled &&
        this.isFullDesignSelected
      ) {
        this.uploadedArtworks = [
          ...this.uploadedArtworks,
          ...fileUploadedPayload.uploadedArtworks,
        ];
      } else {
        this.uploadedArtworks = fileUploadedPayload.uploadedArtworks;
      }
      this.productPrintData = fileUploadedPayload.productPrintData;
      this.isFileValid = fileUploadedPayload.isFileValid;
      this.validationPipelineResults =
        fileUploadedPayload.validationPipelineResults;
      if (
        this.isFullDesignWithArtworksPathEnabled &&
        this.isFullDesignSelected
      ) {
        this.updateNextButton();
      }
    },

    prepareProductVariants() {
      return this.pdpChoices.productVariants.map(
        (v: ProductColorSetterType) => {
          return {
            productName: v.productName,
            variantColor: v.variantColor,
            variantColorName: v.variantColorName,
            colorHex: v.colorHex,
            quantity: v.quantity,
          };
        }
      );
    },

    constructCreatePersonalizationJSON(
      uploadedArtworks: ArtworkResult[]
    ): CreatePersonalizationForm {
      const chosenPrintPositions =
        this.productPrintData.productPrintPositions.filter((p) => {
          const result = this.pdpChoices.productPrintPositions.find(
            (x: ProductPrintPositionSetterType) =>
              x.printPositionSAPCode === p.name
          );

          return result === undefined ? false : true;
        });

      let colorsFromPdpPerPrintPosition = new Map<string, number>();
      this.pdpChoices.productPrintPositions.forEach((position: any) => {
        colorsFromPdpPerPrintPosition.set(
          position.printPositionSAPCode as string,
          position.numberOfColors as number
        );
      });

      let chosenPrintTechniques = new Map<string, PrintTechnique>();

      chosenPrintPositions.forEach((position) => {
        const result = this.pdpChoices.productPrintPositions.find(
          (x: ProductPrintPositionSetterType) =>
            x.printPositionSAPCode === position.name
        );
        const technique = position.printTechniques.find(
          (t) => t.printTechnique.code === result.printTechniqueSAPCode
        );

        if (technique) {
          chosenPrintTechniques.set(position.name, technique);
        }
      });

      const approvedAt =
        this.smallDetailsResult != null &&
        this.smallDetailsResult.approvedAt != null
          ? getUtcDateTime(this.smallDetailsResult.approvedAt)
          : null;

      const positionArtworkMap: {
        printPositionSAPCode: string;
        artworkId: string;
        width: number;
        height: number;
        smallDetails: SmallDetailsRepresentation;
      }[] = this.pdpChoices.productPrintPositions.map(
        (p: ProductPrintPositionSetterType, i: number) => {
          const smallDetailsResult = this.smallDetailsResults.find(
            (x) => x.id === uploadedArtworks[i].personalizationArtworkId
          );

          return {
            printPositionSAPCode: p.printPositionSAPCode,
            artworkId: uploadedArtworks[i].personalizationArtworkId,
            width: p.width,
            height: p.height,
            smallDetails:
              DoSmallDetailsPart &&
              smallDetailsResult &&
              smallDetailsResult.status !== 'NoCheck'
                ? {
                    positiveLoss: smallDetailsResult.positiveDetailsLoss,
                    negativeLoss: smallDetailsResult.negativeDetailsLoss,
                    status: smallDetailsResult.status,
                    calculatedQuality: smallDetailsResult.calculatedQuality,
                    approvedAt: approvedAt,
                  }
                : null,
          };
        }
      );

      const { variants } = this.productPrintData;
      const matchedVariants: ProductPrintVariant[] =
        this.pdpChoices.productVariants.map((pdp: ProductColorSetterType) => {
          return variants.find(
            (variant) => variant.colour === pdp.variantColor
          );
        });

      const variantsByPositions = matchedVariants.map((variant) => {
        return {
          variantColor: variant.colour,
          imageUrl: variant.images.filter((x) => {
            return chosenPrintPositions.find(
              (up) => up.id === x.printPositionID
            );
          }),
        };
      });

      // Prepare data for request payload
      const productVariantsData = this.prepareProductVariants();

      if (
        this.selectedMethod === 'Full design' ||
        this.selectedMethod === 'Full design artworks' ||
        this.selectedMethod === 'Full design - new flow'
      ) {
        return constructFullDesignNewPersonalizationForm(
          this.languageCode,
          this.sapCustomerId,
          this.pdpChoices.masterProductSKU,
          this.uploadedArtworks,
          this.productPrintData,
          chosenPrintPositions,
          chosenPrintTechniques,
          productVariantsData,
          variantsByPositions,
          positionArtworkMap,
          false,
          this.instructions,
          colorsFromPdpPerPrintPosition,
          this.isFullDesignWithArtworksPathEnabled,
          this.isSameLogoForAllPrintPositions
        );
      } else {
        return constructLogoOnlyNewPersonalizationForm(
          this.languageCode,
          this.sapCustomerId,
          this.pdpChoices.masterProductSKU,
          this.uploadedArtworks,
          this.productPrintData,
          chosenPrintPositions,
          chosenPrintTechniques,
          productVariantsData,
          variantsByPositions,
          positionArtworkMap,
          false,
          this.instructions,
          colorsFromPdpPerPrintPosition,
          '',
          '',
          this.customerCompanyLogoUrl
        );
      }
    },

    async createPersonalization(uploadedArtworks: ArtworkResult[]) {
      // This will disable the Add to cart button immediately after clicking it
      this.isCreatePersonalizationSuccessful = true;

      // Generate array of files that are uploaded to each individual print position
      const filesArray = uploadedArtworks.map((a) => {
        return {
          file: base64StringToBlob(a.pngFile, 'image/png'),
        } as VFileUploaderFile;
      });

      const smallDetailsFilesArray = uploadedArtworks.map((a) => {
        return {
          file: new File(
            [base64StringToBlob(a.pngFile, 'image/png')],
            a.personalizationArtworkId
          ),
        } as VFileUploaderFile;
      });

      this.generateColorInstructions();

      // Generate NewPersonalizationForm JSON
      const newPersonalizationForm =
        this.constructCreatePersonalizationJSON(uploadedArtworks);

      // Creates new personalization with the file you uploaded from the logo only flow
      return customersAPI.createPersonalization(
        uploadedArtworks,
        filesArray,
        smallDetailsFilesArray,
        newPersonalizationForm
      );
    },

    async createPersonalizationFullDesign(uploadedArtworks: ArtworkResult[]) {
      // This will disable the Add to cart button immediately after clicking it
      this.isCreatePersonalizationSuccessful = true;

      // Construct NewPersonalizationForm JSON
      const newPersonalizationForm =
        this.constructCreatePersonalizationJSON(uploadedArtworks);

      // Create new full design personalization with the file you uploaded from the full design flow
      if (this.isFullDesignWithArtworksPathEnabled) {
        return customersAPI.createFullDesignPersonalization(
          this.fullDesignMockup,
          newPersonalizationForm
        );
      } else {
        return customersAPI.createFullDesignPersonalization(
          {
            file: base64StringToBlob(
              uploadedArtworks[0].pngFile,
              'application/pdf'
            ),
          } as VFileUploaderFile,
          newPersonalizationForm
        );
      }
    },

    generateSelectedPrintPositionData() {
      return this.pdpChoices.productPrintPositions.map(
        (p: ProductPrintPositionSetterType) => {
          return {
            printPositionCode: p.printPositionSAPCode,
            printTechniqueCode: p.printTechniqueSAPCode,
          } as SelectedPrintPositionData;
        }
      );
    },

    generateColorInstructions() {
      this.pdpChoices.productPrintPositions.forEach(
        (p: ProductPrintPositionSetterType) => {
          this.instructions = this.instructions.concat(
            `${p.printPositionSAPCode} print position: ${p.numberOfColors} colours, `
          );
        }
      );
    },
    handleSameLogo(sameLogo: boolean, position: string) {
      this.isSameLogoForAllPrintPositions = sameLogo;
      this.uploadedArtworks = this.uploadedArtworks.filter(
        (pos) => pos.position === position
      );
      this.artworkPositionMap = this.artworkPositionMap.filter(
        (pos) => pos.position === position
      );
    },
  },

  i18n,
  pinia,
});
